import React, { useContext } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  fontWeights,
  fontSize,
  visuallyHidden,
  zIndexLayers,
  standardTransition,
} from '../styles';
import { Container, Link, HtmlContent, Svg } from './ui';
import Logo from './Logo';
import PhoneArtworkIconLinks from './PhoneArtworkIconLinks';
import Search from './Search';
import Navigation from './Navigation';
import { StoreContext } from '../context/StoreContext';
import personIcon from '../images/person-icon.inline.svg';
import cartIcon from '../images/cart-icon.inline.svg';

const StyledHeader = styled.header`
  position: relative;
  background-color: ${standardColours.white};

  ${maxBreakpointQuery.large`
    z-index: ${zIndexLayers.third};
  `}

  ${({ isSearchOpen }) => {
    if (!isSearchOpen) {
      return css`
        ${minBreakpointQuery.large`
          z-index: ${zIndexLayers.third};
        `}
      `;
    }
  }}
`;

const StyledTop = styled.div`
  padding: 8px 0;
  color: ${standardColours.white};
  background-color: ${brandColours.primary};

  ${maxBreakpointQuery.large`
    ${visuallyHidden()};
  `}
`;

const StyledTopInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 30px;
`;

const StyledSitewideNotice = styled(HtmlContent)`
  p {
    ${fontSize(14)};

    a {
      text-decoration: underline;
    }
  }
`;

const StyledLinksPhone = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const StyledLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
`;

const StyledLink = styled(Link)`
  ${fontSize(14)};
`;

const StyledMiddle = styled.div`
  position: relative;

  ${minBreakpointQuery.large`
    padding-top: 8px;
  `}
`;

const StyledMiddleInner = styled.div`
  display: grid;
  grid-template-columns: 54px 1fr 54px;
  align-items: center;

  ${minBreakpointQuery.tiny`
    grid-template-columns: 59px 1fr 59px;
  `}

  ${minBreakpointQuery.small`
    grid-template-columns: 64px 1fr 64px;
  `}

  ${minBreakpointQuery.large`
    display: flex;
    justify-content: space-between;
    gap: 20px;
  `}

  ${minBreakpointQuery.xlarge`
    gap: 30px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 40px;
  `}

  ${minBreakpointQuery.xxxlarge`
    gap: 60px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    gap: 80px;
  `}
`;

const StyledToggle = styled.button`
  ${maxBreakpointQuery.large`
    position: relative;
    margin: 0;
    padding: 8px 0;
    background: none;
    border: none;
  `}

  ${minBreakpointQuery.large`
    display: none;
  `}

  span {
    ${maxBreakpointQuery.large`
      display: block;
      height: 2px;
      width: 22px;
      margin-top: 4px;
      margin-bottom: 4px;
      background-color: ${brandColours.primary};
      border-radius: 1px;
      transition: ${standardTransition('opacity')}, ${standardTransition(
        'transform',
      )};
    `}

    ${({ isOpen }) => {
      if (isOpen) {
        return css`
          ${maxBreakpointQuery.large`
            opacity: 0;
          `}

          &:first-child {
            ${maxBreakpointQuery.large`
              opacity: 1;
              transform: translateY(6px) rotate(45deg);
            `}
          }

          &:last-child {
            ${maxBreakpointQuery.large`
              opacity: 1;
              transform: translateY(-6px) rotate(-45deg);
            `}
          }
        `;
      }
    }}
  }
`;

const StyledLogo = styled(Logo)`
  width: 180px;

  ${maxBreakpointQuery.large`
    margin: 12px auto;
  `}

  ${minBreakpointQuery.large`
    min-width: 200px;
  `}

  ${minBreakpointQuery.xxlarge`
    min-width: 220px;
  `}
`;

const StyledIconLinksButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${minBreakpointQuery.tiny`
    gap: 15px;
  `}

  ${minBreakpointQuery.small`
    gap: 20px;
  `}

  ${minBreakpointQuery.large`
    order: 4;
    gap: 30px;
  `}

  ${minBreakpointQuery.xlarge`
    gap: 40px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 50px;
  `}
`;

const StyledIconButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;

  ${minBreakpointQuery.xlarge`
    gap: 25px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 30px;
  `}
`;

const StyledIconButton = styled.button`
  position: relative;
  padding: 0;
  background: none;
  border: none;

  svg {
    display: block;
    height: 22px;
    width: 22px;
  }

  span {
    position: absolute;
    top: -8px;
    right: -8px;
    padding: 2px;
    height: 15px;
    min-width: 15px;
    color: ${standardColours.white};
    font-weight: ${fontWeights.semibold};
    ${fontSize(9)};
    line-height: 1;
    text-align: center;
    background-color: ${brandColours.primary};
    border-radius: 50%;
  }

  &:first-child {
    ${maxBreakpointQuery.large`
      ${visuallyHidden()};
    `}
  }
`;

const StyledSearch = styled(Search)`
  ${maxBreakpointQuery.large`
    grid-column: span 3;
    margin-right: -30px;
    margin-left: -30px;
    width: calc(100% + 60px);
    background-color: ${standardColours.lightestGrey};
  `}

  ${minBreakpointQuery.large`
    width: 100%;
  `}

  ${({ isSearchOpen }) => {
    if (isSearchOpen) {
      return css`
        ${minBreakpointQuery.large`
          z-index: ${zIndexLayers.third};
        `}
      `;
    }
  }}
`;

const StyledBottom = styled.div`
  background-color: ${brandColours.septenary};
`;

const StyledBottomInner = styled.div``;

const StyledIconUspsList = styled.ul`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 8px 0;

  ${maxBreakpointQuery.large`
    scroll-snap-type: x mandatory;
    overflow-x: scroll;
  `}
`;

const StyledIconUspsItem = styled.li`
  ${maxBreakpointQuery.large`
    scroll-snap-align: start;
    min-width: calc(100% / 3);
  `}

  ${maxBreakpointQuery.smedium`
    min-width: 50%;
  `}

  ${maxBreakpointQuery.tsmall`
    min-width: 100%;
  `}
`;

const StyledIconUspsLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  ${fontSize(14)};
`;

const StyledIconUspsImage = styled.img`
  height: 18px;
  max-width: 80px;

  ${minBreakpointQuery.small`
    height: 19px;
    max-width: 90px;
  `}

  ${minBreakpointQuery.xxlarge`
    height: 20px;
    max-width: 100px;
  `}
`;

const Header = ({ isNavOpen, setNavOpen }) => {
  const {
    datoCmsHeader: { sitewideNotice, links, iconUsps },
  } = useStaticQuery(graphql`
    query HeaderQuery {
      datoCmsHeader {
        sitewideNotice {
          value
          links {
            id: originalId
            text
            pageUrl {
              ...LinkFragment
            }
          }
        }
        links {
          text
          pageUrl {
            ...LinkFragment
          }
        }
        iconUsps {
          icon {
            url
            alt
          }
          link {
            text
            pageUrl {
              ...LinkFragment
            }
          }
        }
      }
    }
  `);

  const {
    isSearchOpen,
    setSearchOpen,
    toggleCartOpen,
    checkout: { lineItems },
  } = useContext(StoreContext);

  return (
    <StyledHeader isSearchOpen={isSearchOpen}>
      <StyledTop>
        <Container wide={true}>
          <StyledTopInner>
            <StyledSitewideNotice content={sitewideNotice} />
            <StyledLinksPhone>
              <StyledLinks>
                {links.map(({ text, pageUrl }, id) => (
                  <StyledLink key={id} to={pageUrl}>
                    {text}
                  </StyledLink>
                ))}
              </StyledLinks>
            </StyledLinksPhone>
          </StyledTopInner>
        </Container>
      </StyledTop>
      <StyledMiddle>
        <Container wide={true}>
          <StyledMiddleInner>
            <StyledToggle
              aria-label={
                isSearchOpen ? 'Close Search' : 'Open / Close Navigation'
              }
              isOpen={isSearchOpen || isNavOpen}
              onClick={() => {
                if (isSearchOpen) {
                  setSearchOpen(false);
                } else {
                  setNavOpen(!isNavOpen);
                }
              }}
            >
              <span />
              <span />
              <span />
            </StyledToggle>
            <StyledLogo />
            <StyledIconLinksButtons>
              <PhoneArtworkIconLinks isHeader={true} />
              <StyledIconButtons>
                <StyledIconButton
                  as="a"
                  href={`https://${process.env.GATSBY_SHOPIFY_STORE_URL}/account/`}
                  title="View Account"
                  aria-label="View Account"
                >
                  <Svg image={personIcon} />
                </StyledIconButton>
                <StyledIconButton
                  title="Open Cart"
                  aria-label="Open Cart"
                  onClick={toggleCartOpen}
                >
                  <Svg image={cartIcon} />
                  <span>{lineItems.length}</span>
                </StyledIconButton>
              </StyledIconButtons>
            </StyledIconLinksButtons>
            <StyledSearch isSearchOpen={isSearchOpen} />
          </StyledMiddleInner>
        </Container>
        <Navigation isNavOpen={isNavOpen} />
      </StyledMiddle>
      <StyledBottom>
        <Container wide={true}>
          <StyledBottomInner>
            <StyledIconUspsList>
              {iconUsps.map(({ icon, link }, id) => (
                <StyledIconUspsItem key={id}>
                  <StyledIconUspsLink to={link.pageUrl}>
                    <StyledIconUspsImage
                      src={icon.url}
                      alt={icon.alt}
                      loading="lazy"
                    />
                    {link.text}
                  </StyledIconUspsLink>
                </StyledIconUspsItem>
              ))}
            </StyledIconUspsList>
          </StyledBottomInner>
        </Container>
      </StyledBottom>
    </StyledHeader>
  );
};

export default Header;
